import AnalyticsProvider from 'AnalyticsDashboard/components/AnalyticsProvider'
import AnalyticsQuery from 'AnalyticsDashboard/components/AnalyticsQuery'
import ConfidenceIntervalLineChart from 'AnalyticsDashboard/components/ConfidenceIntervalLineChart'

export default function EmailAnalytics() {
  const [option, setOption] = useState('statsOverTime')
  const campaignId = useSel((s) => s.local.campaignId)
  const emailBlastId = useSel((s) => s.local.emailBlastId)
  const options = [
    { id: 'segmentOpens', label: 'Open Rate Benchmark' },
    { id: 'statsOverTime', label: 'Stats Over Time' },
    { id: 'emailProvider', label: 'Email Providers' },
  ]

  return <div>
    <div>
      <div className='tabs'>
        {options.map((opt) => (
          <div className={`tab ${option === opt.id ? 'selected' : ''}`} key={opt.id} onClick={() => setOption(opt.id)}>
            {opt.label}
          </div>
        ))}
      </div>
    </div>
    <div>
      <AnalyticsProvider uiEnabled={false} filterValues={{ campaign_id: campaignId }}>
        { option === 'segmentOpens' && <AnalyticsQuery kind='emails_predicted_opens' chartType='custom'
          metrics={[{ id: 'estimatedOpenRateAtThreeDays' }, { id: 'historicOpenRateAtThreeDays' }, { id: 'confidenceIntervalOpenRate' }, { id: 'count' }, { id: 'varianceOpens' }, { id: 'countExpectedOpens' }, { id: 'estimatedClickRateAtThreeDays' }, { id: 'historicClickRateAtThreeDays' }, { id: 'confidenceIntervalClickRate' }, { id: 'countActualClicks' }, { id: 'countExpectedClicks' }, { id: 'varianceClicks' }]}
          dimensions={[{ id: 'hour' }, { id: 'areas_uk_region' }]}
          primaryDimension={{ id: 'hour', label: 'Hours since send' }}
          secondaryDimension={{ id: 'areasUkRegion', label: 'Region' }}
          parameters={{ emailBlastId }}
          chartOptions={{ columnMapping: {
            openRate: { valueColumn: 'estimatedOpenRateAtThreeDays', meanColumn: 'historicOpenRateAtThreeDays', confidenceIntervalColumn: 'confidenceIntervalOpenRate' },
            clickRate: { valueColumn: 'estimatedClickRateAtThreeDays', meanColumn: 'historicClickRateAtThreeDays', confidenceIntervalColumn: 'confidenceIntervalClickRate' } }, normalize: false }}
          chartComponent={SpecialAnalyticsShow}
          /> }
        { option === 'statsOverTime' && <AnalyticsQuery kind='emails' chartType='line'
          metrics={[{ id: 'count', cumulative: true }, { id: 'countOpened', cumulative: true }, { id: 'countClicked', cumulative: true }]}
          dimensions={[{ id: 'time', timeBucket: 'minute' }]}
          primaryDimension={{ id: 'time', label: 'Time' }}
          /> }
        { option === 'emailProvider' && <AnalyticsQuery kind='emails' chartType='table'
          metrics={[{ id: 'count' }, { id: 'countOpened' }, { id: 'countClicked' }]}
          dimensions={[{ id: 'targets_email_provider' }]}
          primaryDimension={{ id: 'targets_email_provider', label: 'Email Provider' }}
        /> }
      </AnalyticsProvider>
    </div>
  </div>
}

function SpecialAnalyticsShow({ data, chartOptions, query,  }) {
  const [state, setState] = useState('collapsed')

  const subChartOptions = (() => {
    if (state === 'collapsed') return chartOptions;
    if (state === 'openRate') return { ...chartOptions, columnMapping: chartOptions.columnMapping.openRate };
    if (state === 'clickRate') return { ...chartOptions, columnMapping: chartOptions.columnMapping.clickRate };
  })()

  function onClickMetric(metricKey) {
    console.log('Clicked metric:', metricKey);
    if (metricKey === 'openRate') setState('openRate');
    else if (metricKey === 'clickRate') setState('clickRate');
    else setState('collapsed');
  }

  return <div>
    { state !== 'collapsed' && <div><button className='button naked' onClick={() => setState('collapsed')}>Back</button></div> }
    { state === 'collapsed' && <ConfidenceIntervalSummary data={data} chartOptions={chartOptions} query={query} onClickMetric={onClickMetric} /> }
    { state === 'openRate' && <ConfidenceIntervalLineChart data={data} chartOptions={subChartOptions} query={query} />}
    { state === 'clickRate' && <ConfidenceIntervalLineChart data={data} chartOptions={subChartOptions} query={query} /> }
  </div>
}

function ConfidenceIntervalSummary({ data, chartOptions, query, onClickMetric = () => {} }) {
  const { columnMapping, normalize = false } = chartOptions;
  const { secondaryDimension } = query;

  // Group data by secondary dimension, or use all data if no dimension
  let groupedData = {};

  if (!secondaryDimension?.id) {
    groupedData = { 'All Data': data };
  } else {
    data.forEach(row => {
      const dimensionValue = row[secondaryDimension.id] || '(Not specified)';
      if (!groupedData[dimensionValue]) {
        groupedData[dimensionValue] = [];
      }
      groupedData[dimensionValue].push(row);
    });
  }

  // Sort dimension values for consistent ordering
  const dimensionValues = Object.keys(groupedData).sort((a, b) => {
    if (a === 'All Data') return -1;
    if (b === 'All Data') return 1;
    if (a === '(Not specified)') return 1;
    if (b === '(Not specified)') return -1;
    return a.localeCompare(b);
  });

  // Get metrics from columnMapping
  const metrics = Object.entries(columnMapping).map(([key, mapping]) => ({
    key,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim(),
    mapping
  }));

  // Calculate stats for each dimension and metric
  const summaryData = dimensionValues.map(dimensionValue => {
    const categoryData = groupedData[dimensionValue];
    const sortedData = [...categoryData].sort((a, b) => a.hour - b.hour);
    const lastPoint = sortedData[sortedData.length - 1];

    const metricStats = metrics.map(metric => {
      const actual = lastPoint[metric.mapping.valueColumn];
      const expected = lastPoint[metric.mapping.meanColumn];
      const confidenceInterval = lastPoint[metric.mapping.confidenceIntervalColumn];

      const upperBound = expected + confidenceInterval;
      const lowerBound = expected - confidenceInterval;

      let normalizedActual, normalizedExpected, normalizedUpper, normalizedLower;

      if (normalize) {
        normalizedExpected = 0;
        normalizedActual = expected === 0 ? 0 : (actual - expected) / expected;
        normalizedUpper = expected === 0 ? 0 : (upperBound - expected) / expected;
        normalizedLower = expected === 0 ? 0 : (lowerBound - expected) / expected;
      }

      const displayActual = normalize ? normalizedActual : actual;
      const displayExpected = normalize ? normalizedExpected : expected;
      const displayUpper = normalize ? normalizedUpper : upperBound;
      const displayLower = normalize ? normalizedLower : lowerBound;

      // Determine color based on position relative to confidence interval
      let color, label;
      if (displayActual < displayLower) {
        color = 'rgb(220, 38, 38)';
        label = 'Below CI';
      } else if (displayActual < displayExpected) {
        color = 'rgb(249, 115, 22)';
        label = 'Lower Half';
      } else if (displayActual <= displayUpper) {
        color = 'rgb(234, 179, 8)';
        label = 'Upper Half';
      } else {
        color = 'rgb(34, 197, 94)';
        label = 'Above CI';
      }

      return {
        metricKey: metric.key,
        metricLabel: metric.label,
        actual: displayActual,
        expected: displayExpected,
        upper: displayUpper,
        lower: displayLower,
        color,
        label
      };
    });

    return {
      category: dimensionValue,
      hour: lastPoint.hour,
      metrics: metricStats
    };
  });

  const formatValue = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div className="confidence-interval-summary">
      <div className="summary-header">
        <h2 className="summary-title">
          {secondaryDimension?.label ? `Final Performance by ${secondaryDimension.label}` : 'Final Performance'}
        </h2>
        <div className="summary-legend">
          <span className="legend-item legend-red">Below CI</span>
          <span className="legend-item legend-orange">Lower Half</span>
          <span className="legend-item legend-yellow">Upper Half</span>
          <span className="legend-item legend-green">Above CI</span>
        </div>
      </div>

      <div className="summary-list">
        {summaryData.map(item => (
          <div key={item.category} className="summary-item">
            <div className="summary-category">
              <div className="category-name">{item.category}</div>
              <div className="category-hour">Hour {item.hour}</div>
            </div>

            <div className="summary-stats">
              {item.metrics.map(metric => (
                <div key={metric.metricKey} className="stat-block" onClick={() => onClickMetric(metric.metricKey)}>
                  <div className="stat-label">{metric.metricLabel}</div>
                  <div className="stat-content">
                    <div className="stat-values">
                      <div className="value-actual" style={{ color: metric.color }}>
                        {formatValue(metric.actual)}
                      </div>
                      <div className="value-expected">
                        Expected: {formatValue(metric.expected)}
                      </div>
                      <div className="value-status" style={{ color: metric.color }}>
                        {metric.label}
                      </div>
                    </div>

                    <div className="confidence-bar-vertical">
                      <div className="confidence-range-vertical">
                        <div className="confidence-scale">
                          <span className="scale-label scale-top">{formatValue(metric.upper)}</span>
                          <span className="scale-label scale-bottom">{formatValue(metric.lower)}</span>
                        </div>
                        <div className="confidence-track">
                          <div
                            className="confidence-interval-vertical"
                            style={{
                              bottom: '0%',
                              height: '100%'
                            }}
                          />
                          <div
                            className="confidence-expected-vertical"
                            style={{
                              bottom: `${((metric.expected - metric.lower) / (metric.upper - metric.lower)) * 100}%`
                            }}
                          />
                          <div
                            className="confidence-actual-vertical"
                            style={{
                              bottom: `${Math.max(0, Math.min(100, ((metric.actual - metric.lower) / (metric.upper - metric.lower)) * 100))}%`,
                              backgroundColor: metric.color
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
